import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Logger } from "@base-provider";
import SelectEntityStep from "./selectEntityStep";
import UploadFileStep from "./uploadFileStep";
import UploadFileResultsStep from "./uploadFileResultsStep";
import MatchDataStep from "./matchDataStep";
import {
  uploadFile,
  getUploadedFileStatus,
} from "../../services/datasetsService";
import { STATUS } from "../../constants/status";
import UploadFileProgress from "./uploadFileProgress";
import { getAllEntities } from "../../services/entitiesService";
import { useTranslation } from "react-i18next";
import FileUploadHandler from "../../helpers/fileUploadHandler";
import { fileUploadActions } from "../../redux/reducers/fileUploadReducer";
import { COLUMN_MAPPING_STATUS_ACTION } from "../../constants/columnMappingStatus";
//core imports
import { SFDeleteForeverIcon } from "../../../components/icons";
import getStyled from "../../../hooks/getStyled";
import SFStepper from "../../../components/stepper";
import SFStep from "../../../components/stepper/step";
import SFLoadingSpinner from "../../../components/loadingSpinner";
import SFErrorPlaceholder from "../../../components/errorPlaceholder";

const { styled } = getStyled();

export let FileHandler = new FileUploadHandler();

const StyledWrapper = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  //calculated height based on stepper actionButtons bar
  height: "calc(100% - 60px)",
}));

const FileUploadWizard = (props) => {
  const [entity, setEntity] = useState(null);
  const [error, setError] = useState(false);
  const [fileStatus, setFileStatus] = useState({
    status: STATUS.PENDING,
    message: "",
    showProgress: false,
  });

  useEffect(() => {
    dispatch(fileUploadActions.resetData());
    FileHandler.clearFile();
    if (props.entityName) {
      const getEntities = async () => {
        let res = await getAllEntities();

        if (res.data.length == 0) {
          setError(
            `Entity <strong>${props.entityName}</strong> does not exist`
          );
        }
        setEntity(res.data.find((x) => x.name === props.entityName));
      };
      getEntities();
    }
  }, []);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const cancelWizardHandler = () => {
    FileHandler.clearFile();
    dispatch(fileUploadActions.resetData());
    props.onCancel();
  };

  const finishHandler = async (stepperData) => {
    Logger.of("App.uploadFile.finishHandler").info({ data: stepperData });
    if (FileHandler.fileData.length > 0) {
      setFileStatus((curState) => ({ ...curState, showProgress: true }));
      const uploadRes = await uploadFileHandler(stepperData);
      if (uploadRes && uploadRes.data.transactionId.length > 0) {
        if (props.onBeginDatasetUpload) {
          await datasetUploadHandler(uploadRes.data);
        }

        //TODO : this check handler should be refactored
        const result = await checkUploadedStatusHandler(
          uploadRes.data.transactionId
        );

        if (result) {
          setFileStatus((curState) => ({ ...curState, showProgress: false }));
          props.onFinish();
          FileHandler.clearFile();
          return true;
        } else {
          return false;
        }
      }
    }
  };
  const timeout = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const checkUploadedStatusHandler = async (
    transactionId,
    externalCheck = false
  ) => {
    try {
      if (!externalCheck) {
        const res = await getUploadedFileStatus(transactionId);
        Logger.of("App.uploadFile.checkLoadStatusHandler").info({
          response: res,
        });

        if (
          res.data.status !== STATUS.LOADED &&
          res.data.status !== STATUS.FAILED
        ) {
          await timeout(5000);
          return checkUploadedStatusHandler(transactionId);
        } else if (res.data.status === STATUS.LOADED) {
          await timeout(5000);
          return checkUploadedStatusHandler(transactionId, true);
        } else if (res.data.status === STATUS.FAILED) {
          setFileStatus((curState) => ({
            ...curState,
            status: res.data.status,
            message:
              res.data.status === STATUS.FAILED
                ? res.data.logs
                : curState.message,
          }));
          return false;
        }
      } else {
        if (props.onEndDatasetUpload) {
          const res = await props.onEndDatasetUpload(transactionId);
          if (res.wait) {
            await timeout(5000);
            return checkUploadedStatusHandler(transactionId, true);
          } else if (res.messages) {
            setFileStatus((curState) => ({
              ...curState,
              status: STATUS.FAILED,
              message: res.messages,
            }));
            return false;
          } else {
            setFileStatus((curState) => ({
              ...curState,
              status: STATUS.LOADED,
              message: res.messages,
            }));
            return true;
          }
        } else {
          setFileStatus((curState) => ({
            ...curState,
            status: STATUS.LOADED,
          }));
          return true;
        }
      }
    } catch (error) {
      setFileStatus((curState) => ({
        ...curState,
        status: STATUS.FAILED,
      }));
      return false;
    }
  };

  //BEGIN DATASET UPLOAD
  const datasetUploadHandler = async (data) => {
    try {
      const res = await props.onBeginDatasetUpload(data);
      Logger.of(
        "App.uploadFile.matchDataStep.onBeginDatasetUploadHandler"
      ).info({
        res,
      });
      return true;
    } catch (error) {
      setFileStatus((curState) => ({
        ...curState,
        status: STATUS.FAILED,
      }));
    }
  };

  const uploadFileHandler = async (stepperData) => {
    try {
      const formData = new FormData();
      formData.append("file", FileHandler.file);
      var tenantId = FileHandler.getTenantId();
      tenantId && formData.append("tenantId", tenantId);
      formData.append(
        "configuration",
        JSON.stringify(
          stepperData[1].data.mappedData.filter(
            (x) => x.action === COLUMN_MAPPING_STATUS_ACTION.CONFIRMED
          )
        )
      );
      formData.append(
        "entityId",
        stepperData[0].data?.entity?.entity?.id ?? entity.id
      );
      const res = await uploadFile(formData);
      Logger.of("App.uploadFile.matchDataStep.uploadFileHandler").info({
        res,
      });
      return res;
    } catch (error) {
      return false;
    }
  };

  const onCloseModal = () => {
    setFileStatus((curState) => ({
      ...curState,
      status: STATUS.PENDING,
      showProgress: false,
    }));
  };

  if (error !== false) {
    return <SFErrorPlaceholder message={error} />;
  }
  if (!entity && props.entityName) {
    return <SFLoadingSpinner />;
  }

  return (
    <StyledWrapper>
      <UploadFileProgress
        open={fileStatus.showProgress}
        fileStatus={fileStatus}
        onClose={onCloseModal}
      />
      <SFStepper
        dialogTitle={t("fileUploadWizard.dialog.header")}
        onFinish={finishHandler}
        keepFinishDialogOpen={false} // Do not keep the confirmation dialog open because we have a dedicated dialog to track the upload progress
        onCancel={cancelWizardHandler}
        name={props.name ?? props.entityName}
        confirmationMessages={{
          submit: t("fileUploadWizard.dialog.confirmText"),
        }}
        actions={[
          {
            label: t("fileUploadWizard.dialog.cancelBtn"),
            onClick: cancelWizardHandler,
            icon: <SFDeleteForeverIcon size={"small"} />,
          },
        ]}
      >
        {!props.entityName && (
          <SFStep label={t("fileUploadWizard.selectEntity")}>
            <SelectEntityStep />
          </SFStep>
        )}
        <SFStep
          label={t("fileUploadWizard.uploadfile", {
            name: props.name?.toLowerCase() ?? props.entityName?.toLowerCase(),
          })}
        >
          <UploadFileStep
            entity={entity}
            templateName={props.templateName}
            name={props.name ?? props.entityName}
            acceptedFileTypes={
              props.acceptedFileTypes ? props.acceptedFileTypes.join(",") : ""
            }
          />
        </SFStep>
        <SFStep label={t("fileUploadWizard.matchData")}>
          <MatchDataStep entity={entity} />
        </SFStep>
        <SFStep label={t("fileUploadWizard.reviewAndSubmit")}>
          <UploadFileResultsStep />
        </SFStep>
      </SFStepper>
    </StyledWrapper>
  );
};

FileUploadWizard.propTypes = {
  acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
  name: PropTypes.string,
  templateName: PropTypes.string,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  entityName: PropTypes.string,
  onBeginDatasetUpload: PropTypes.func,
  onEndDatasetUpload: PropTypes.func,
};

export default FileUploadWizard;
